import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import TreeView from 'devextreme-react/tree-view';
import {
	cfiNavigation,
	dealerNavigation,
	distributorNavigation,
	eeProNavigation,
	manufacturerNavigation,
	serviceProviderNavigation
} from '../../app-navigation';
import { useNavigation } from '../../contexts/navigation';
// import { useScreenSize } from '../../utils/media-query';
import './SideNavigationMenu.scss';
import type { SideNavigationMenuProps } from '../../types';

import * as events from 'devextreme/events';
import { useAuth } from "../../contexts/auth";
import { CompanyTypes } from "../../types/enums";
import { StatisticsItem, useGetStatistics } from '../../api/statistics';
import { useGetUnreadAnnouncementCount } from '../../api/announcements';
import Button from 'devextreme-react/button';
import UserInfo from '../user-info/UserInfo';
import { useScreenSize } from '../../utils/media-query';
import { useGetCompany } from '../../api/company';
import { useGetMyAccountInformation } from '../../features/common/my-account/api/get-my-account-information';

export default function SideNavigationMenu(props: React.PropsWithChildren<SideNavigationMenuProps>) {
	const {
		children,
		selectedItemChanged,
		openMenu,
		compactMode,
		onMenuReady,
		showToggleMenuButton
	} = props;
	const { user } = useAuth();
	const { isTiny, isXSmall, isSmall } = useScreenSize();

	const statistics = useGetStatistics(user?.companyId!, user?.userName!, user?.companyTypeId!);
	const unreadAnnouncementCount = useGetUnreadAnnouncementCount(user?.userName!);
	const company = useGetCompany(user?.companyId!);
	const myAccountInfo = useGetMyAccountInformation();

	function processMenuItems(items: any[], statisticsData: StatisticsItem[], announcementCount: number, companyData: any, userData: any): any[] {
		const findStatTotal = (statID: number) => {
			if (!statID || !statisticsData) return 0;
			return statisticsData.find(s => s.statID === statID)?.total ?? 0;
		};

		return items.map(item => {
			const isVisible = item.visible instanceof Function ? item.visible(companyData, userData) : (item.visible ?? true);

			if (!isVisible) {
				return { ...item, visible: false };
			}

			let processedChildren: any[] = [];
			let finalTotal = 0;

			if (item.items && item.items.length > 0) {
				processedChildren = processMenuItems(item.items, statisticsData, announcementCount, companyData, userData);
				finalTotal = processedChildren.reduce((sum, child) => {
					return sum + (child.finalTotal || 0);
				}, 0);
			} else {
				finalTotal = findStatTotal(item.statID);
			}

			if (item.statID === 10300) {
				finalTotal = announcementCount;
			}

			let html = item.icon ? `<i class="dx-icon dx-icon-${item.icon}"></i>` : '';
			html += item.text;

			if (item.statID) {
				let countHtml = ` (${finalTotal})`;
				if (item.statID === 10300 && announcementCount > 0) {
					countHtml = ` <span class="highlighted-counter">(${announcementCount})</span>`;
				}
				html += countHtml;
			}

			const finalItem = {
				...item,
				expanded: item.expanded,
				path: item.path && !(/^\//.test(item.path)) ? `/${item.path}` : item.path,
				items: processedChildren,
				visible: true,
				html: html,
				finalTotal: finalTotal
			};
			delete finalItem.text;
			delete finalItem.icon;
			return finalItem;
		}).filter(item => item.visible);
	}

	function generateMenuItems() {
		let navigation = [] as any[];

		if (company.data && myAccountInfo.data) {
			switch (user?.companyTypeId) {
				case CompanyTypes.CFI: {
					navigation = cfiNavigation;
					break;
				}
				case CompanyTypes.DEALER: {
					navigation = dealerNavigation;
					break;
				}
				case CompanyTypes.DISTRIBUTOR: {
					navigation = distributorNavigation;
					break;
				}
				case CompanyTypes.MFR: {
					navigation = manufacturerNavigation;
					break;
				}
				case CompanyTypes.SERVICE_PROVIDER: {
					navigation = serviceProviderNavigation;
					break;
				}
				case CompanyTypes.EEPRO: {
					navigation = eeProNavigation;
					break;
				}
			}

			// Ensure installerTypes is an array to avoid null reference errors
			const safeCompanyData = {
				...company.data,
				installerTypes: company.data.installerTypes ?? []
			};

			return processMenuItems(navigation, statistics.data, unreadAnnouncementCount.data ?? 0, safeCompanyData, myAccountInfo.data);
		}
		else {
			return navigation;
		}
	}

	const menuItems = useMemo(
		generateMenuItems,
		[statistics.data, unreadAnnouncementCount.data, user, company.data, myAccountInfo.data]
	);

	const { navigationData: { currentPath } } = useNavigation();

	const treeViewRef = useRef<TreeView>(null);
	const wrapperRef = useRef<HTMLDivElement>();
	const getWrapperRef = useCallback((element: HTMLDivElement) => {
		const prevElement = wrapperRef.current;
		if (prevElement) {
			events.off(prevElement, 'dxclick');
		}

		wrapperRef.current = element;
		events.on(element, 'dxclick', (e: React.PointerEvent) => {
			openMenu(e);
		});
	}, [openMenu]);

	useEffect(() => {
		const treeView = treeViewRef.current && treeViewRef.current.instance;
		if (!treeView) {
			return;
		}

		if (currentPath !== undefined) {
			treeView.selectItem(currentPath);
		}

		if (currentPath === '/home') {
			treeView.unselectAll();
		}

	}, [currentPath, compactMode]);

	return (
		<div
			className={'dx-swatch-additional side-navigation-menu'}
			ref={getWrapperRef}
		>
			<Button icon="spinnext" onClick={props.toggleMenu} className="toggle-menu-button" visible={showToggleMenuButton} />
			{children}
			<div className={'menu-container'}>
				{
					(isTiny || isXSmall || isSmall) &&
					<div>
						<UserInfo />
					</div>
				}
				<TreeView
					ref={treeViewRef}
					items={menuItems}
					keyExpr={'path'}
					selectionMode={'single'}
					focusStateEnabled={false}
					onItemClick={selectedItemChanged}
					onContentReady={onMenuReady}
					width={'100%'}
					height={'100%'}
					selectByClick={true}
				/>
			</div>
		</div>
	);
}
